import type { BrightcovePlayer } from "@brightcove/player-loader"
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import styled from "styled-components"
import { useAuth } from "~/features/auth/useAuth"
import type { DetailResult } from "~/features/detail/types"
import { Button } from "~/shared/ui"
import { useControlsVisibility } from "../hooks/useControlsVisibility"
import { usePlayerEngine } from "../hooks/usePlayerEngine"
import { useThumbnailCues } from "../hooks/useThumbnailCues"
import { playerLog } from "../lib/playerLog"
import { BrightcoveVideo } from "./BrightcoveVideo"
import { PlayerControls } from "./PlayerControls"

/**
 * Fixed rather than in the document flow.
 *
 * A player is full-bleed on every surface it appears on, and `apps/web` is a
 * federated remote — in the App Shell the host owns the header, nav and footer,
 * and the player has to cover them. Escaping the layout here is what makes the
 * same component correct standalone and embedded.
 */
const Stage = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.player.surface};
  color: ${({ theme }) => theme.player.textPrimary};
  z-index: ${({ theme }) => theme.zIndex.overlay};
`

const Centred = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  z-index: ${({ theme }) => theme.zIndex.overlay};
`

const Message = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.player.textPrimary};
`

const SubMessage = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.player.textStrong};
`

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`

/** `Button`'s outline variant is drawn for the light page and disappears here. */
const GhostButton = styled(Button)`
  color: ${({ theme }) => theme.player.textPrimary};
  border-color: ${({ theme }) => theme.player.textStrong};

  &:hover:not(:disabled) {
    background-color: rgb(255 255 255 / 0.12);
  }
`

/** Purely an indicator: it must never take a click away from the controls under it. */
const Buffering = styled(Centred)`
  pointer-events: none;
`

const Spinner = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid rgb(255 255 255 / 0.25);
  border-top-color: ${({ theme }) => theme.player.brand};
  border-radius: ${({ theme }) => theme.radius.pill};
  animation: player-spin 900ms linear infinite;

  @keyframes player-spin {
    to {
      rotate: 360deg;
    }
  }
`

interface PlayerPageProps {
  readonly detail: DetailResult
  /** From the route. Decides which session loads first. */
  readonly sessionId: string | undefined
  /** Where the back control and a fatal error return to, e.g. `/study/abc`. */
  readonly detailHref: string
  readonly sessionsTitle: string
  readonly notFoundMessage: string
  readonly qaHook: string
}

/**
 * The playback screen for both studies and digital events.
 *
 * It owns which session is playing — the session rail switches it here rather
 * than navigating, so moving between sessions swaps the Brightcove source in
 * place instead of unmounting the player. The URL is kept in step with
 * `replace`, so a reload or a shared link still lands on the right session while
 * Back still leaves the player rather than walking through every session watched.
 */
export const PlayerPage = ({
  detail,
  sessionId,
  detailHref,
  sessionsTitle,
  notFoundMessage,
  qaHook,
}: PlayerPageProps): ReactElement => {
  const navigate = useNavigate()
  const { userId } = useAuth()
  const { view, isLoading, isError } = detail

  const [activeSessionId, setActiveSessionId] = useState<string | undefined>(sessionId)
  const [reloadKey, setReloadKey] = useState(0)

  const sessions = useMemo(() => view?.sessions ?? [], [view])
  const activeSession = useMemo(
    () => sessions.find(session => session.id === activeSessionId) ?? sessions.find(session => session.playback),
    [sessions, activeSessionId],
  )
  const playback = activeSession?.playback

  const engine = usePlayerEngine({
    fallbackDurationSeconds: playback?.durationSeconds,
    startSeconds: playback?.playheadSeconds ?? 0,
    videoCloudId: playback?.videoCloudId ?? "",
  })
  const { state, commands, attach, fail, reset } = engine

  const visibility = useControlsVisibility(state.status === "playing")
  const cues = useThumbnailCues(playback?.thumbnailsUrl)

  const exit = useCallback(() => void navigate(detailHref), [navigate, detailHref])

  const selectSession = useCallback(
    (nextId: string) => {
      const next = sessions.find(session => session.id === nextId)

      if (!next?.playback || next.id === activeSession?.id) {
        return
      }

      setActiveSessionId(nextId)
      visibility.closeSessions()
      void navigate(next.href, { replace: true })
    },
    [sessions, activeSession?.id, visibility, navigate],
  )

  const retry = useCallback(() => {
    reset()
    setReloadKey(key => key + 1)
  }, [reset])

  // A route change from outside this component — a browser Back into an earlier
  // session, say — has to win over the local selection.
  useEffect(() => {
    if (sessionId) {
      setActiveSessionId(sessionId)
    }
  }, [sessionId])

  /**
   * Autoplay, attempted once per session.
   *
   * The TV player just plays. A browser may refuse without a prior gesture, and
   * that refusal is not an error — `play()` swallows it and the viewer sees the
   * centre play button, which is the correct fallback. Muting to force autoplay
   * would be worse for teaching content than one click.
   */
  const autoPlayedRef = useRef<string | null>(null)
  useEffect(() => {
    const id = playback?.videoCloudId

    if (!id || state.status !== "paused" || state.durationSeconds <= 0 || autoPlayedRef.current === id) {
      return
    }

    autoPlayedRef.current = id
    playerLog("autoplay attempt", { videoCloudId: id, durationSeconds: state.durationSeconds })
    commands.play()
  }, [playback?.videoCloudId, state.status, state.durationSeconds, commands])

  /**
   * TEMPORARY, with `playerLog`. The spinner is shown for exactly one reason —
   * `status === "loading"` — so printing the status the page is actually
   * rendering separates "the engine never heard from the player" from "the
   * engine knows, and the chrome is drawing the wrong thing".
   */
  useEffect(() => {
    playerLog("page renders status", state.status)
  }, [state.status])

  const handleReady = useCallback(
    (player: BrightcovePlayer) => {
      attach(player)
    },
    [attach],
  )

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      const target = event.target

      // Never steal a key from a control that has its own handling for it.
      if (target instanceof HTMLElement && (target.tagName === "BUTTON" || target.getAttribute("role") === "slider")) {
        if (event.key !== "Escape") {
          return
        }
      }

      switch (event.key) {
        case " ":
        case "k":
          event.preventDefault()
          commands.togglePlay()
          break
        case "ArrowLeft":
          commands.skip(-10)
          break
        case "ArrowRight":
          commands.skip(10)
          break
        case "c":
          commands.toggleCaptions()
          break
        case "Escape":
          if (visibility.hasOpenLayer) {
            visibility.closeSessions()
            visibility.closeSettings()
          } else {
            exit()
          }
          break
        default:
          return
      }

      visibility.notifyActivity()
    }

    window.addEventListener("keydown", onKeyDown)

    return () => window.removeEventListener("keydown", onKeyDown)
  }, [commands, visibility, exit])

  if (isLoading) {
    return (
      <Stage data-qa-hook={`${qaHook}-loading`}>
        <Centred>
          <Spinner />
        </Centred>
      </Stage>
    )
  }

  if (isError || !view) {
    return (
      <Stage data-qa-hook={`${qaHook}-error`}>
        <Centred>
          <Message>{notFoundMessage}</Message>
          <Button type="button" onClick={exit} data-qa-hook={`${qaHook}-error-back`}>
            Back to details
          </Button>
        </Centred>
      </Stage>
    )
  }

  if (!activeSession || !playback) {
    return (
      <Stage data-qa-hook={`${qaHook}-unplayable`}>
        <Centred>
          <Message>This session isn't available to play.</Message>
          <SubMessage>It may not be included with your library access.</SubMessage>
          <Button type="button" onClick={exit} data-qa-hook={`${qaHook}-unplayable-back`}>
            Back to details
          </Button>
        </Centred>
      </Stage>
    )
  }

  return (
    <Stage
      data-qa-hook={qaHook}
      onPointerMove={visibility.notifyActivity}
      onPointerDown={visibility.notifyActivity}
      style={{ cursor: visibility.isChromeVisible ? "default" : "none" }}
    >
      <BrightcoveVideo
        accountId={playback.accountId}
        videoCloudId={playback.videoCloudId}
        viewerId={userId}
        reloadKey={reloadKey}
        onReady={handleReady}
        onFailure={fail}
      />

      {state.status === "failed" ? (
        <Centred data-qa-hook={`${qaHook}-playback-error`}>
          <Message>Something went wrong playing this session.</Message>
          <SubMessage>Try again, or go back and pick another session.</SubMessage>
          <Actions>
            <Button type="button" onClick={retry} data-qa-hook={`${qaHook}-retry`}>
              Try again
            </Button>
            <GhostButton type="button" $variant="outline" onClick={exit} data-qa-hook={`${qaHook}-playback-error-back`}>
              Back to details
            </GhostButton>
          </Actions>
        </Centred>
      ) : (
        <>
          {state.status === "loading" ? (
            <Buffering data-qa-hook={`${qaHook}-buffering`}>
              <Spinner />
            </Buffering>
          ) : null}

          <PlayerControls
            title={view.title}
            subTitle={activeSession.title}
            state={state}
            commands={commands}
            visibility={visibility}
            cues={cues}
            sessions={sessions}
            currentSessionId={activeSession.id}
            sessionsTitle={sessionsTitle}
            onSelectSession={selectSession}
            onExit={exit}
          />
        </>
      )}
    </Stage>
  )
}
